import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useAuth } from '../lib/auth-context.js';
import { useOrg } from '../lib/org-context.js';
import { apiFetch, ApiError } from '../lib/api.js';
import { useToast } from '../components/Toast.js';
import { formatUserName } from '@crm98/shared';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface TeamUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  role: string;
  status: string;
  business_ids: string[];
}

interface Business {
  id: string;
  name: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  expiresAt: string;
  createdAt: string;
}

/* ------------------------------------------------------------------ */
/*  TeamPage                                                           */
/* ------------------------------------------------------------------ */

export function TeamPage() {
  const { orgId: authOrgId } = useAuth();
  const { viewingOrgId, userRole } = useOrg();
  const { toast } = useToast();

  const orgId = viewingOrgId ?? authOrgId;

  const [users, setUsers] = useState<TeamUser[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editingAssignments, setEditingAssignments] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [confirmCancelInvite, setConfirmCancelInvite] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!orgId) return;
    try {
      const data = await apiFetch<{ users: TeamUser[] }>(`/orgs/${orgId}/users`);
      setUsers(data.users);
    } catch {
      toast('error', 'Failed to load team members');
    } finally {
      setIsLoading(false);
    }
  }, [orgId, toast]);

  const fetchBusinesses = useCallback(async () => {
    if (!orgId) return;
    try {
      const data = await apiFetch<{ businesses: { id: string; legalName: string }[] }>(`/orgs/${orgId}/businesses`);
      setBusinesses(data.businesses.map(b => ({ id: b.id, name: b.legalName })));
    } catch { /* non-critical */ }
  }, [orgId]);

  const fetchInvitations = useCallback(async () => {
    if (!orgId) return;
    try {
      const data = await apiFetch<{ invitations: PendingInvitation[] }>(`/orgs/${orgId}/invitations`);
      setInvitations(data.invitations);
    } catch { /* non-critical */ }
  }, [orgId]);

  useEffect(() => {
    fetchUsers();
    fetchBusinesses();
    fetchInvitations();
  }, [fetchUsers, fetchBusinesses, fetchInvitations]);

  const handleChangeRole = async (userId: string, newRole: string) => {
    try {
      await apiFetch(`/orgs/${orgId}/users/${userId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole }),
      });
      toast('success', 'Role updated');
      fetchUsers();
    } catch {
      toast('error', 'Failed to update role');
    }
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      await apiFetch(`/orgs/${orgId}/users/${userId}`, { method: 'DELETE' });
      toast('success', 'Team member removed');
      setConfirmRemove(null);
      fetchUsers();
    } catch {
      toast('error', 'Failed to remove team member');
    }
  };

  const handleReactivateUser = async (userId: string) => {
    try {
      await apiFetch(`/orgs/${orgId}/users/${userId}/reactivate`, { method: 'PUT' });
      toast('success', 'User reactivated');
      fetchUsers();
    } catch {
      toast('error', 'Failed to reactivate user');
    }
  };

  const handleUpdateAssignments = async (userId: string, businessIds: string[]) => {
    try {
      await apiFetch(`/orgs/${orgId}/users/${userId}/businesses`, {
        method: 'PUT',
        body: JSON.stringify({ business_ids: businessIds }),
      });
      toast('success', 'Business assignments updated');
      setEditingAssignments(null);
      fetchUsers();
    } catch {
      toast('error', 'Failed to update assignments');
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await apiFetch(`/orgs/${orgId}/invitations/${invitationId}/resend`, { method: 'POST' });
      toast('success', 'Invitation resent');
      fetchInvitations();
    } catch {
      toast('error', 'Failed to resend invitation');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await apiFetch(`/orgs/${orgId}/invitations/${invitationId}`, { method: 'DELETE' });
      toast('success', 'Invitation cancelled');
      setConfirmCancelInvite(null);
      fetchInvitations();
    } catch {
      toast('error', 'Failed to cancel invitation');
    }
  };

  // Only admins / owners can manage team
  if (userRole === 'staff') {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-sm">You do not have permission to manage the team.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Team</h2>
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-48 mb-2" />
            <div className="h-3 bg-gray-100 rounded w-32" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Team</h2>
          <p className="text-sm text-gray-500">Manage your team members</p>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Invite Team Member
        </button>
      </div>

      {/* User list */}
      {users.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">No team members yet</h3>
          <p className="text-sm text-gray-500 mb-4">Invite your first team member to get started.</p>
          <button
            onClick={() => setShowInvite(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            Invite Team Member
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Name / Email</th>
                  <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Role</th>
                  <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Businesses</th>
                  <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Status</th>
                  <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">
                        {formatUserName({ firstName: user.first_name, lastName: user.last_name, email: user.email })}
                      </p>
                      {(user.first_name || user.last_name) && (
                        <p className="text-xs text-gray-500">{user.email}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <RoleBadge role={user.role} />
                    </td>
                    <td className="px-4 py-3">
                      <BusinessList
                        businessIds={user.business_ids}
                        businesses={businesses}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <StatusDot status={user.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {user.status === 'locked' ? (
                          <button
                            onClick={() => handleReactivateUser(user.id)}
                            className="text-xs text-green-600 hover:text-green-700 font-medium bg-green-50 px-2 py-1 rounded"
                          >
                            Reactivate
                          </button>
                        ) : (
                          <>
                            <select
                              value={user.role}
                              onChange={e => handleChangeRole(user.id, e.target.value)}
                              className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white"
                            >
                              <option value="admin">Admin</option>
                              <option value="staff">Staff</option>
                            </select>
                            <button
                              onClick={() => setEditingAssignments(user.id)}
                              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                            >
                              Assign
                            </button>
                            <button
                              onClick={() => setConfirmRemove(user.id)}
                              className="text-xs text-red-500 hover:text-red-700 font-medium"
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {users.map(user => (
              <div key={user.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {formatUserName({ firstName: user.first_name, lastName: user.last_name, email: user.email })}
                    </p>
                    {(user.first_name || user.last_name) && (
                      <p className="text-xs text-gray-500">{user.email}</p>
                    )}
                  </div>
                  <StatusDot status={user.status} />
                </div>
                <div className="flex items-center gap-2">
                  <RoleBadge role={user.role} />
                  <BusinessList businessIds={user.business_ids} businesses={businesses} />
                </div>
                <div className="flex items-center gap-3 pt-1">
                  {user.status === 'locked' ? (
                    <button
                      onClick={() => handleReactivateUser(user.id)}
                      className="text-xs text-green-600 hover:text-green-700 font-medium bg-green-50 px-2 py-1 rounded"
                    >
                      Reactivate
                    </button>
                  ) : (
                    <>
                      <select
                        value={user.role}
                        onChange={e => handleChangeRole(user.id, e.target.value)}
                        className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white"
                      >
                        <option value="admin">Admin</option>
                        <option value="staff">Staff</option>
                      </select>
                      <button
                        onClick={() => setEditingAssignments(user.id)}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Assign
                      </button>
                      <button
                        onClick={() => setConfirmRemove(user.id)}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Pending Invitations</h3>
            <span className="inline-flex items-center justify-center text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full px-2 py-0.5">
              {invitations.length}
            </span>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            {/* Desktop table */}
            <div className="hidden md:block">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Name / Email</th>
                    <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Role</th>
                    <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Sent</th>
                    <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Expires</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map(inv => (
                    <tr key={inv.id} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">
                          {formatUserName({ firstName: inv.firstName, lastName: inv.lastName, email: inv.email })}
                        </p>
                        {(inv.firstName || inv.lastName) && (
                          <p className="text-xs text-gray-500">{inv.email}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <RoleBadge role={inv.role} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-500">{formatTimeAgo(inv.createdAt)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-500">{new Date(inv.expiresAt).toLocaleDateString()}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {confirmCancelInvite === inv.id ? (
                            <>
                              <span className="text-xs text-gray-500">Cancel invite?</span>
                              <button
                                onClick={() => handleCancelInvitation(inv.id)}
                                className="text-xs text-red-600 hover:text-red-700 font-medium"
                              >
                                Yes
                              </button>
                              <button
                                onClick={() => setConfirmCancelInvite(null)}
                                className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                              >
                                No
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleResendInvitation(inv.id)}
                                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                              >
                                Resend
                              </button>
                              <button
                                onClick={() => setConfirmCancelInvite(inv.id)}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                                title="Cancel invitation"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {invitations.map(inv => (
                <div key={inv.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {formatUserName({ firstName: inv.firstName, lastName: inv.lastName, email: inv.email })}
                      </p>
                      {(inv.firstName || inv.lastName) && (
                        <p className="text-xs text-gray-500">{inv.email}</p>
                      )}
                    </div>
                    <RoleBadge role={inv.role} />
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{formatTimeAgo(inv.createdAt)}</span>
                    <span>Expires {new Date(inv.expiresAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-3 pt-1">
                    {confirmCancelInvite === inv.id ? (
                      <>
                        <span className="text-xs text-gray-500">Cancel invite?</span>
                        <button
                          onClick={() => handleCancelInvitation(inv.id)}
                          className="text-xs text-red-600 hover:text-red-700 font-medium"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setConfirmCancelInvite(null)}
                          className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                        >
                          No
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleResendInvitation(inv.id)}
                          className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Resend
                        </button>
                        <button
                          onClick={() => setConfirmCancelInvite(inv.id)}
                          className="text-xs text-red-500 hover:text-red-700 font-medium"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && orgId && (
        <InviteModal
          orgId={orgId}
          businesses={businesses}
          onClose={() => setShowInvite(false)}
          onSuccess={() => {
            setShowInvite(false);
            fetchUsers();
            fetchInvitations();
          }}
        />
      )}

      {/* Assignment Editor Modal */}
      {editingAssignments && (() => {
        const editUser = users.find(u => u.id === editingAssignments);
        return editUser ? (
          <AssignmentModal
            user={editUser}
            businesses={businesses}
            onClose={() => setEditingAssignments(null)}
            onSave={(ids) => handleUpdateAssignments(editingAssignments, ids)}
          />
        ) : null;
      })()}

      {/* Remove Confirmation */}
      {confirmRemove && (
        <ConfirmDialog
          title="Remove Team Member"
          message={`Are you sure you want to remove ${users.find(u => u.id === confirmRemove)?.email ?? 'this user'}? They will lose access immediately.`}
          confirmLabel="Remove"
          onConfirm={() => handleRemoveUser(confirmRemove)}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatTimeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function RoleBadge({ role }: { role: string }) {
  const isAdmin = role === 'admin' || role === 'owner';
  return (
    <span
      className={`inline-flex text-xs font-medium px-2 py-0.5 rounded-full ${
        isAdmin
          ? 'bg-blue-100 text-blue-700'
          : 'bg-gray-100 text-gray-600'
      }`}
    >
      {role.charAt(0).toUpperCase() + role.slice(1)}
    </span>
  );
}

function StatusDot({ status }: { status: string }) {
  const config: Record<string, { color: string; label: string }> = {
    active: { color: 'bg-green-500', label: 'Active' },
    invited: { color: 'bg-yellow-500', label: 'Invited' },
    locked: { color: 'bg-red-500', label: 'Locked' },
  };
  const s = config[status] ?? { color: 'bg-gray-400', label: status };
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
      <span className={`w-2 h-2 rounded-full ${s.color}`} />
      {s.label}
    </span>
  );
}

function BusinessList({ businessIds, businesses }: { businessIds: string[]; businesses: Business[] }) {
  if (!businessIds || businessIds.length === 0) {
    return <span className="text-xs text-gray-400">All</span>;
  }
  const names = businessIds
    .map(id => businesses.find(b => b.id === id)?.name)
    .filter(Boolean);
  if (names.length === 0) return <span className="text-xs text-gray-400">--</span>;
  return (
    <span className="text-xs text-gray-600">
      {names.length <= 2
        ? names.join(', ')
        : `${names[0]}, +${names.length - 1} more`}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Invite Modal                                                       */
/* ------------------------------------------------------------------ */

function InviteModal({
  orgId,
  businesses,
  onClose,
  onSuccess,
}: {
  orgId: string;
  businesses: Business[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { toast } = useToast();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'staff'>('staff');
  const [selectedBusinesses, setSelectedBusinesses] = useState<string[]>(
    // Default: if only 1 business, auto-select it
    businesses.length === 1 ? [businesses[0]!.id] : [],
  );
  const [allBusinesses, setAllBusinesses] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const toggleBusiness = (id: string) => {
    setSelectedBusinesses(prev =>
      prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id],
    );
  };

  const handleAllBusinessesToggle = () => {
    if (allBusinesses) {
      setAllBusinesses(false);
      setSelectedBusinesses([]);
    } else {
      setAllBusinesses(true);
      setSelectedBusinesses(businesses.map(b => b.id));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !firstName || !lastName) return;
    setIsSending(true);
    try {
      await apiFetch(`/orgs/${orgId}/users/invite`, {
        method: 'POST',
        body: JSON.stringify({
          email,
          first_name: firstName,
          last_name: lastName,
          role,
          business_ids: role === 'staff' ? (selectedBusinesses.length > 0 ? selectedBusinesses : businesses.map(b => b.id)) : [],
        }),
      });
      toast('success', `Invitation sent to ${firstName}`);
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'invitation_pending') {
          toast('error', 'An invitation is already pending for this email. Cancel it first to resend.');
        } else if (err.code === 'user_exists') {
          toast('error', 'This email already has an account in your organization.');
        } else {
          toast('error', err.message);
        }
      } else {
        toast('error', 'Failed to send invitation');
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Invite Team Member</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">First name</label>
              <input
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                required
                autoFocus
                placeholder="Jane"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Last name</label>
              <input
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                required
                placeholder="Smith"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Email address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="colleague@studio.com"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
            <select
              value={role}
              onChange={e => setRole(e.target.value as 'admin' | 'staff')}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
            >
              <option value="admin">Admin</option>
              <option value="staff">Staff</option>
            </select>
          </div>

          {role === 'staff' && businesses.length > 1 && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">
                Business Access
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allBusinesses}
                    onChange={handleAllBusinessesToggle}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-900">All Businesses</span>
                </label>
                {!allBusinesses && businesses.map(biz => (
                  <label key={biz.id} className="flex items-center gap-2 cursor-pointer pl-4">
                    <input
                      type="checkbox"
                      checked={selectedBusinesses.includes(biz.id)}
                      onChange={() => toggleBusiness(biz.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">{biz.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isSending || !email}
            className="w-full bg-primary-600 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSending ? 'Sending...' : 'Send Invitation'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Assignment Modal                                                   */
/* ------------------------------------------------------------------ */

function AssignmentModal({
  user,
  businesses,
  onClose,
  onSave,
}: {
  user: TeamUser;
  businesses: Business[];
  onClose: () => void;
  onSave: (ids: string[]) => void;
}) {
  const allIds = businesses.map(b => b.id);
  const initialAll = user.business_ids?.length === businesses.length && businesses.length > 1;
  const [selected, setSelected] = useState<string[]>(user.business_ids ?? allIds);
  const [allSelected, setAllSelected] = useState(initialAll);

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id];
      setAllSelected(next.length === businesses.length);
      return next;
    });
  };

  const handleAllToggle = () => {
    if (allSelected) {
      setAllSelected(false);
      setSelected([]);
    } else {
      setAllSelected(true);
      setSelected(allIds);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Assign Businesses
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-gray-500">
          Select which businesses <span className="font-medium text-gray-700">{formatUserName({ firstName: user.first_name, lastName: user.last_name, email: user.email })}</span> can access.
        </p>

        <div className="space-y-2 max-h-60 overflow-y-auto">
          {businesses.length === 0 ? (
            <p className="text-sm text-gray-400">No businesses configured.</p>
          ) : businesses.length === 1 ? (
            <p className="text-sm text-gray-600">
              Assigned to: <span className="font-medium">{businesses[0]!.name}</span>
            </p>
          ) : (
            <>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleAllToggle}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-900">All Businesses</span>
              </label>
              {!allSelected && businesses.map(biz => (
                <label key={biz.id} className="flex items-center gap-2 cursor-pointer pl-4">
                  <input
                    type="checkbox"
                    checked={selected.includes(biz.id)}
                    onChange={() => toggle(biz.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">{biz.name}</span>
                </label>
              ))}
            </>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 border border-gray-200 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(selected.length > 0 ? selected : allIds)}
            className="flex-1 bg-primary-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Confirm Dialog                                                     */
/* ------------------------------------------------------------------ */

function ConfirmDialog({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 border border-gray-200 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
